import Hyperblobs from 'hyperblobs'
import b4a from 'b4a'
import { randomBytes, encrypt, decrypt, derive } from '@pine/crypto'

const CHUNK = 64 * 1024

/*
 * How long a download may go without a single new block before it is given
 * up on — not how long it may take. It used to be a flat minute for the whole
 * file, and a video on mobile data that was arriving perfectly well was still
 * arriving at sixty seconds: the app declared it unavailable in the middle of
 * the download. Progress resets the clock; only silence runs it out.
 */
const STALL_TIMEOUT = 45000

/**
 * Media store for one room.
 *
 * Each member writes attachments to their *own* blob core — a Corestore
 * keypair is derived from that device's primary key, so there is no shared
 * writable core to fight over. An attachment therefore has to say which core
 * its bytes live in, and readers open that core by key and pull the blocks
 * over the room's existing replication streams.
 *
 * On top of that, every file gets its own random key. That key rides inside
 * the message, which is already inside the room's encrypted cores, so:
 *   - forwarding a photo costs 32 bytes, not a re-upload;
 *   - a peer can serve blob blocks for a room it belongs to without being
 *     able to decrypt a file it was never sent.
 */
export class BlobStore {
  constructor (store, { encryptionKey = null, name = 'blobs' } = {}) {
    this.store = store
    this.encryptionKey = encryptionKey ? derive(encryptionKey, 'pine-blb', 7) : null

    this.core = store.get({ name, encryptionKey: this.encryptionKey })
    this.blobs = new Hyperblobs(this.core, { blockSize: CHUNK })

    this._remote = new Map() // core key hex -> { core, blobs }
    this._prefetched = new Map() // blob id -> the download already asked for
    this.opening = null
  }

  /**
   * Ask for an attachment's blocks now, without reading them.
   *
   * Deliberately not get(): that decrypts the whole file into memory, which
   * is the right thing when it is about to be shown and a waste of a phone's
   * RAM when it is only being kept. This asks the core for the block range
   * and lets it arrive on disk.
   *
   * Asked once per attachment, and never given up on. If the sender is not
   * online, the range stays requested and hypercore fills it the moment they
   * are — which is exactly the point, since that is when the photo can come.
   */
  prefetch (attachment) {
    const key = blobKey(attachment)
    if (!key) return Promise.resolve(false)
    if (this._prefetched.has(key)) return this._prefetched.get(key)

    const job = this._prefetch(attachment).catch(() => false)
    this._prefetched.set(key, job)
    return job
  }

  async _prefetch (attachment) {
    await this.ready()
    const id = decodeId(attachment.blobId)
    if (!id) return false
    if (await this.has(attachment)) return true

    const entry = await this._coreFor(attachment.core)
    if (!entry || typeof entry.core.download !== 'function') return false
    const download = entry.core.download({
      start: id.blockOffset,
      end: id.blockOffset + id.blockLength
    })
    await download.done()
    return true
  }

  ready () {
    if (!this.opening) this.opening = this.core.ready()
    return this.opening
  }

  get key () {
    return this.core.key
  }

  /**
   * @param data   Uint8Array | Blob | ReadableStream | string
   * @returns attachment descriptor ready to embed in a message
   */
  async put (data, { onProgress = null } = {}) {
    await this.ready()
    const bytes = await toBytes(data, onProgress)
    const key = randomBytes(32)
    const id = await this.blobs.put(encrypt(bytes, key))
    return {
      blobId: encodeId(id),
      core: this.core.key,
      key,
      size: bytes.byteLength
    }
  }

  /**
   * Fetch and decrypt an attachment.
   *
   * @param timeout  how long to wait with nothing arriving — see STALL_TIMEOUT.
   * @param onProgress  called with a fraction between 0 and 1 as blocks land,
   *   so a photo on a slow link shows it is coming rather than a bare spinner.
   * @throws an error with code 'ATTACHMENT_UNAVAILABLE' when nobody who has
   *   it has been reachable for that long. Nothing is cancelled: the blocks
   *   stay asked for, and arrive whenever the sender is back.
   */
  async get (attachment, { timeout = STALL_TIMEOUT, onProgress = null } = {}) {
    await this.ready()
    const id = decodeId(attachment.blobId)
    if (!id) return null

    const entry = await this._coreFor(attachment.core)
    if (!entry) return null
    const { core, blobs } = entry

    const start = id.blockOffset
    const end = id.blockOffset + id.blockLength
    let have = 0
    let heard = Date.now()

    const report = () => {
      if (onProgress && id.blockLength) {
        try { onProgress(Math.min(1, have / id.blockLength)) } catch {}
      }
    }
    const onDownload = (index) => {
      if (index < start || index >= end) return
      heard = Date.now()
      have++
      report()
    }

    core.on('download', onDownload)
    try {
      if (onProgress) {
        for (let i = start; i < end; i++) if (await core.has(i)) have++
        report()
      }

      const ciphertext = await untilStalled(blobs.get(id), timeout, () => heard)
      if (!ciphertext) return null

      const key = toKey(attachment.key)
      if (!key) return ciphertext
      const plaintext = decrypt(ciphertext, key)
      if (!plaintext) throw new Error('Attachment failed to decrypt')
      return plaintext
    } finally {
      core.off('download', onDownload)
    }
  }

  /** True when every block of the attachment is already on this device. */
  async has (attachment) {
    await this.ready()
    const id = decodeId(attachment.blobId)
    if (!id) return false
    const entry = await this._coreFor(attachment.core)
    if (!entry) return false
    for (let i = id.blockOffset; i < id.blockOffset + id.blockLength; i++) {
      if (!(await entry.core.has(i))) return false
    }
    return true
  }

  /** Drop the local copy of one attachment but keep the message. */
  async clear (attachment) {
    await this.ready()
    const id = decodeId(attachment.blobId)
    if (!id) return
    const entry = await this._coreFor(attachment.core)
    if (!entry || !entry.core.clear) return
    await entry.core.clear(id.blockOffset, id.blockOffset + id.blockLength).catch(() => {})
  }

  async _coreFor (coreKey) {
    const key = toKey(coreKey)
    if (!key || b4a.equals(key, this.core.key)) {
      return { core: this.core, blobs: this.blobs }
    }

    const hex = b4a.toString(key, 'hex')
    const cached = this._remote.get(hex)
    if (cached) return cached

    const core = this.store.get({ key, encryptionKey: this.encryptionKey })
    await core.ready()
    const entry = { core, blobs: new Hyperblobs(core, { blockSize: CHUNK }) }
    this._remote.set(hex, entry)
    return entry
  }

  async close () {
    for (const entry of this._remote.values()) {
      await entry.blobs.close().catch(() => {})
      await entry.core.close().catch(() => {})
    }
    this._remote.clear()
    await this.blobs.close().catch(() => {})
    await this.core.close().catch(() => {})
  }
}

/* Hyperblobs ids are plain objects; messages carry them as 16 packed bytes. */

export function encodeId (id) {
  const buf = b4a.allocUnsafe(16)
  writeU32(buf, id.blockOffset, 0)
  writeU32(buf, id.blockLength, 4)
  writeU32(buf, id.byteOffset, 8)
  writeU32(buf, id.byteLength, 12)
  return buf
}

export function decodeId (value) {
  if (!value) return null
  const buf = typeof value === 'string' ? b4a.from(value, 'base64') : value
  if (buf.byteLength !== 16) return null
  return {
    blockOffset: readU32(buf, 0),
    blockLength: readU32(buf, 4),
    byteOffset: readU32(buf, 8),
    byteLength: readU32(buf, 12)
  }
}

function writeU32 (buf, n, offset) {
  buf[offset] = n & 0xff
  buf[offset + 1] = (n >>> 8) & 0xff
  buf[offset + 2] = (n >>> 16) & 0xff
  buf[offset + 3] = (n >>> 24) & 0xff
}

function readU32 (buf, offset) {
  return (buf[offset] | (buf[offset + 1] << 8) | (buf[offset + 2] << 16) | (buf[offset + 3] << 24)) >>> 0
}

function toKey (value) {
  if (!value) return null
  if (typeof value === 'string') return b4a.from(value, 'hex')
  return value
}

/** One stable string per attachment, whichever form its id arrived in. */
function blobKey (attachment) {
  if (!attachment || !attachment.blobId) return null
  const id = attachment.blobId
  const core = attachment.core
  const idPart = typeof id === 'string' ? id : b4a.toString(id, 'base64')
  const corePart = !core ? '' : (typeof core === 'string' ? core : b4a.toString(core, 'hex'))
  return corePart + '/' + idPart
}

/**
 * A blob read never fails on its own — it just waits for a peer that has the
 * blocks. The UI needs to be able to say "not available" instead of spinning,
 * but only once nothing has arrived for a while: a slow download is still a
 * download.
 */
function untilStalled (promise, ms, lastHeard) {
  if (!ms) return promise
  return new Promise((resolve, reject) => {
    const check = () => {
      const quiet = Date.now() - lastHeard()
      if (quiet >= ms) {
        const error = new Error('Attachment is not available yet — nobody online has it')
        error.code = 'ATTACHMENT_UNAVAILABLE'
        reject(error)
        return
      }
      timer = setTimeout(check, ms - quiet)
    }
    let timer = setTimeout(check, ms)
    promise.then(
      (value) => { clearTimeout(timer); resolve(value) },
      (err) => { clearTimeout(timer); reject(err) }
    )
  })
}

async function toBytes (data, onProgress) {
  if (data instanceof Uint8Array) return data
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    const buf = new Uint8Array(await data.arrayBuffer())
    if (onProgress) onProgress(1)
    return buf
  }
  if (data && typeof data.getReader === 'function') {
    const reader = data.getReader()
    const chunks = []
    let total = 0
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      chunks.push(value)
      total += value.byteLength
      if (onProgress) onProgress(total)
    }
    return b4a.concat(chunks)
  }
  if (typeof data === 'string') return b4a.from(data, 'utf8')
  throw new TypeError('Unsupported blob input')
}
